import {
  type ChangeEvent,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AddRounded,
  CloseRounded,
  KeyboardArrowDownRounded,
  KeyboardArrowUpRounded,
  RemoveRounded,
  SearchOutlined,
} from '@mui/icons-material';
import {
  SpreadsheetViewerSearchInput,
  SpreadsheetViewerSearchToolbarSection,
  SpreadsheetViewerToolbarButton,
  SpreadsheetViewerToolbarDivider,
  SpreadsheetViewerToolbarSection,
  SpreadsheetViewerToolbarText,
  SpreadsheetViewerWorkbookActions,
} from 'components/FileViewer/spreadsheet-viewer/SpreadsheetViewer.styles';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import type { TabNavigationItem } from 'components/ui/TabNavigation';
import { getZoomAdjustedPx } from 'components/ui/utilities/zoom';
import { useZoom } from 'context/ZoomContext';
import { isFindKeyboardShortcut, isScopedViewerKeyboardShortcut } from '../viewerKeyboard.utils';
import { applySpreadsheetFreezePane } from './SpreadsheetWorkbookPreview.freezePane.utils';
import { resolveSpreadsheetWorkbookPreviewFeatureOptions } from './SpreadsheetWorkbookPreview.options';
import type { SpreadsheetWorkbookPreviewProps } from './SpreadsheetWorkbookPreview.props';
import SpreadsheetWorkbookViewer from './SpreadsheetWorkbookViewer';

const XLSX_MAX_ZOOM = 2;
const XLSX_MIN_ZOOM = 0.5;
const XLSX_COLUMN_INDICATOR_HEIGHT = 12;
const XLSX_COLUMN_LEGEND_HEIGHT = 25;
const XLSX_FROZEN_REFERENCE_ROW_TOP = XLSX_COLUMN_INDICATOR_HEIGHT + XLSX_COLUMN_LEGEND_HEIGHT;
const XLSX_ROW_INDICATOR_COVER_WIDTH = 13.5;
const XLSX_SEARCH_CURRENT_MATCH_CLASS = 'xlsx-search-match-current';
const XLSX_SEARCH_MATCH_CLASS = 'xlsx-search-match';
const XLSX_SEARCH_BUTTON_WIDTH = 24;
const XLSX_SEARCH_COUNTER_WIDTH = 30;
const XLSX_ZOOM_STEP = 0.25;

const clampXlsxZoomScale = (zoomScale: number) => Math.min(XLSX_MAX_ZOOM, Math.max(XLSX_MIN_ZOOM, zoomScale));

const getSpreadsheetSheetTabValue = (tabId: string | undefined, index: number, tabValuePrefix: string): string =>
  tabId ? String(tabId) : `${tabValuePrefix}-${index}`;

const getElementContentWidth = (element: HTMLElement) => Math.max(element.scrollWidth, element.clientWidth);

const getElementVisualScale = (element: HTMLElement) => {
  if (!element.offsetWidth) {
    return 1;
  }

  return element.getBoundingClientRect().width / element.offsetWidth;
};

const getSpreadsheetFitContentWidth = (content: HTMLDivElement) => {
  const spreadsheetGridWidths = Array.from(content.querySelectorAll<HTMLElement>('.data-grid.spreadsheet')).map(
    getElementContentWidth
  );
  const widestSpreadsheetGrid = Math.max(0, ...spreadsheetGridWidths);
  if (widestSpreadsheetGrid > 0) {
    return widestSpreadsheetGrid;
  }

  const previewContent = content.querySelector<HTMLElement>('[data-testid="spreadsheet-note-xlsx-preview"]');
  const measuredContent = previewContent ?? content;

  return getElementContentWidth(measuredContent);
};

const normalizeSpreadsheetSearchText = (text: string) => text.trim().toLocaleLowerCase();

const getSpreadsheetSearchableCells = (content: HTMLDivElement): HTMLElement[] =>
  Array.from(content.querySelectorAll<HTMLElement>('.data-grid td:not(.legend)')).filter(
    cell => !cell.closest('[aria-hidden="true"]')
  );

const clearSpreadsheetSearchClasses = (cells: HTMLElement[]) => {
  cells.forEach(cell => {
    cell.classList.remove(XLSX_SEARCH_MATCH_CLASS, XLSX_SEARCH_CURRENT_MATCH_CLASS);
  });
};

const SpreadsheetWorkbookPreview = ({
  actions,
  activeTabIndex,
  ariaLabel,
  compensateAppZoom = false,
  contentVariant = 'legacy-data-grid',
  emptyMessage,
  emptyTitle,
  error,
  errorMessage,
  errorTitle,
  fallback,
  featureOptions,
  freezePaneColumnCellKey,
  freezePaneRowCellKey,
  isLoading = false,
  loadingMessage,
  onActiveTabIndexChange,
  onViewerHeightChange,
  resetKey,
  searchShortcutScope = 'viewer',
  tabValuePrefix = 'spreadsheet-preview-sheet',
  tabs,
  testId,
  workbookContextValue = {},
}: SpreadsheetWorkbookPreviewProps) => {
  const { zoomLevel } = useZoom();
  const controlsZoomLevel = compensateAppZoom ? zoomLevel : 1;
  const searchControlsZoomLevel = controlsZoomLevel;
  const activeSpreadsheetContentRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const searchMatchesRef = useRef<HTMLElement[]>([]);
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const previousResetKeyRef = useRef(resetKey);
  const [activeSearchMatchIndex, setActiveSearchMatchIndex] = useState(0);
  const [activeSpreadsheetContentElement, setActiveSpreadsheetContentElement] = useState<HTMLDivElement | null>(null);
  const [isSpreadsheetFitMode, setIsSpreadsheetFitMode] = useState(false);
  const [isSpreadsheetSearchOpen, setIsSpreadsheetSearchOpen] = useState(false);
  const [openedTabIndexes, setOpenedTabIndexes] = useState<Set<number>>(() => new Set([activeTabIndex]));
  const [spreadsheetSearchMatchCount, setSpreadsheetSearchMatchCount] = useState(0);
  const [spreadsheetSearchQuery, setSpreadsheetSearchQuery] = useState('');
  const [spreadsheetZoomScale, setSpreadsheetZoomScale] = useState(1);
  const resolvedFeatureOptions = useMemo(
    () => resolveSpreadsheetWorkbookPreviewFeatureOptions(featureOptions),
    [featureOptions]
  );
  const effectiveSpreadsheetZoomScale = resolvedFeatureOptions.zoom ? spreadsheetZoomScale : 1;
  const normalizedSearchQuery = useMemo(
    () => normalizeSpreadsheetSearchText(spreadsheetSearchQuery),
    [spreadsheetSearchQuery]
  );

  const activeTab = tabs[activeTabIndex] ?? null;
  const shouldEnableFrozenReferenceColumn =
    contentVariant === 'legacy-data-grid' && resolvedFeatureOptions.stickyReferenceColumn;
  const shouldEnableFrozenReferenceRow =
    contentVariant === 'legacy-data-grid' && resolvedFeatureOptions.stickyReferenceRow;
  const activeSheetTabValue = activeTab
    ? getSpreadsheetSheetTabValue(activeTab.id, activeTabIndex, tabValuePrefix)
    : false;
  const sheetTabs = useMemo<TabNavigationItem[]>(
    () =>
      tabs.map((tab, index) => ({
        badge: tab.badge,
        disabled: tab.disabled || tab.isActionTab,
        label: tab.label ?? `Sheet ${index + 1}`,
        value: getSpreadsheetSheetTabValue(tab.id, index, tabValuePrefix),
      })),
    [tabs, tabValuePrefix]
  );

  const activateSheetTab = useCallback(
    (nextIndex: number) => {
      onActiveTabIndexChange(nextIndex);
      setOpenedTabIndexes(currentIndexes => {
        if (currentIndexes.has(nextIndex)) {
          return currentIndexes;
        }
        const nextIndexes = new Set(currentIndexes);
        nextIndexes.add(nextIndex);

        return nextIndexes;
      });
    },
    [onActiveTabIndexChange]
  );

  const handleActiveSpreadsheetContentRef = useCallback((element: HTMLDivElement | null) => {
    activeSpreadsheetContentRef.current = element;
    setActiveSpreadsheetContentElement(element);
  }, []);

  const handleSheetTabChange = useCallback(
    (value: string) => {
      const nextIndex = tabs.findIndex(
        (tab, index) => getSpreadsheetSheetTabValue(tab.id, index, tabValuePrefix) === value
      );
      const nextTab = tabs[nextIndex];
      if (nextIndex === -1 || nextTab?.disabled || nextTab?.isActionTab) {
        return;
      }
      activateSheetTab(nextIndex);
    },
    [activateSheetTab, tabs, tabValuePrefix]
  );

  const calculateSpreadsheetFitZoomScale = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    const content = activeSpreadsheetContentRef.current;
    if (!scrollContainer || !content) {
      return 1;
    }

    const contentWidth = getSpreadsheetFitContentWidth(content);
    if (contentWidth <= 0) {
      return 1;
    }

    const availableWidth = Math.max(1, scrollContainer.clientWidth - 24);

    return clampXlsxZoomScale(availableWidth / contentWidth);
  }, []);

  // Reset viewer-local state when switching to another document/workbook.
  useEffect(() => {
    if (previousResetKeyRef.current === resetKey) {
      return;
    }
    previousResetKeyRef.current = resetKey;
    setOpenedTabIndexes(new Set([activeTabIndex]));
    setIsSpreadsheetFitMode(false);
    setIsSpreadsheetSearchOpen(false);
    setActiveSearchMatchIndex(0);
    setSpreadsheetSearchMatchCount(0);
    setSpreadsheetSearchQuery('');
    setSpreadsheetZoomScale(1);
  }, [activeTabIndex, resetKey]);

  // External navigation can change the active tab without going through the tab bar.
  useEffect(() => {
    setOpenedTabIndexes(currentIndexes => {
      if (currentIndexes.has(activeTabIndex)) {
        return currentIndexes;
      }
      const nextIndexes = new Set(currentIndexes);
      nextIndexes.add(activeTabIndex);

      return nextIndexes;
    });
  }, [activeTabIndex]);

  // When a consumer disables zoom controls, keep the workbook rendered at 1x even if prior state existed.
  useEffect(() => {
    if (resolvedFeatureOptions.zoom) {
      return;
    }

    setIsSpreadsheetFitMode(false);
    setSpreadsheetZoomScale(1);
  }, [resolvedFeatureOptions.zoom]);

  // Keep the search input keyboard-ready as soon as the toolbar opens.
  useEffect(() => {
    if (!isSpreadsheetSearchOpen) {
      return;
    }

    searchInputRef.current?.focus();
    searchInputRef.current?.select();
  }, [isSpreadsheetSearchOpen]);

  // Keep the selected sheet valid when the processed workbook changes.
  useEffect(() => {
    if (!tabs.length) {
      return;
    }
    if (activeTabIndex < tabs.length && !tabs[activeTabIndex]?.disabled && !tabs[activeTabIndex]?.isActionTab) {
      return;
    }
    const firstAvailableIndex = tabs.findIndex(tab => !tab.disabled && !tab.isActionTab);
    activateSheetTab(firstAvailableIndex === -1 ? 0 : firstAvailableIndex);
  }, [activeTabIndex, activateSheetTab, tabs]);

  // Search is applied to the mounted active sheet so workbook parsing and citation mapping stay unchanged.
  useEffect(() => {
    clearSpreadsheetSearchClasses(searchMatchesRef.current);
    searchMatchesRef.current = [];

    if (!resolvedFeatureOptions.search || !normalizedSearchQuery || !activeSpreadsheetContentElement) {
      setSpreadsheetSearchMatchCount(0);
      setActiveSearchMatchIndex(0);
      return undefined;
    }

    const matches = getSpreadsheetSearchableCells(activeSpreadsheetContentElement).filter(cell =>
      normalizeSpreadsheetSearchText(cell.textContent ?? '').includes(normalizedSearchQuery)
    );
    const currentMatchIndex = matches.length ? Math.min(activeSearchMatchIndex, matches.length - 1) : 0;

    if (currentMatchIndex !== activeSearchMatchIndex) {
      setActiveSearchMatchIndex(currentMatchIndex);
    }

    matches.forEach(cell => cell.classList.add(XLSX_SEARCH_MATCH_CLASS));
    matches[currentMatchIndex]?.classList.add(XLSX_SEARCH_CURRENT_MATCH_CLASS);
    matches[currentMatchIndex]?.scrollIntoView({ block: 'center', inline: 'center' });
    searchMatchesRef.current = matches;
    setSpreadsheetSearchMatchCount(matches.length);

    return () => clearSpreadsheetSearchClasses(matches);
  }, [
    activeSearchMatchIndex,
    activeSpreadsheetContentElement,
    activeTabIndex,
    effectiveSpreadsheetZoomScale,
    normalizedSearchQuery,
    resolvedFeatureOptions.search,
  ]);

  // Consumers that virtualize rows need the live viewer height, including drawer resize.
  useEffect(() => {
    if (!onViewerHeightChange) {
      return undefined;
    }

    const element = viewerRef.current;
    if (!element) {
      return undefined;
    }

    const updateHeight = () => {
      onViewerHeightChange(Math.max(1, Math.floor(element.getBoundingClientRect().height)));
    };

    if (typeof ResizeObserver === 'undefined') {
      updateHeight();
      return undefined;
    }

    const resizeObserver = new ResizeObserver(updateHeight);

    resizeObserver.observe(element);
    updateHeight();

    return () => resizeObserver.disconnect();
  }, [onViewerHeightChange]);

  const handleSpreadsheetZoomOut = useCallback(() => {
    setIsSpreadsheetFitMode(false);
    setSpreadsheetZoomScale(currentZoomScale => clampXlsxZoomScale(currentZoomScale - XLSX_ZOOM_STEP));
  }, []);

  const handleSpreadsheetZoomIn = useCallback(() => {
    setIsSpreadsheetFitMode(false);
    setSpreadsheetZoomScale(currentZoomScale => clampXlsxZoomScale(currentZoomScale + XLSX_ZOOM_STEP));
  }, []);

  const handleSpreadsheetFit = useCallback(() => {
    setIsSpreadsheetFitMode(true);
    setSpreadsheetZoomScale(calculateSpreadsheetFitZoomScale());
  }, [calculateSpreadsheetFitZoomScale]);

  const handleSpreadsheetActualSize = useCallback(() => {
    setIsSpreadsheetFitMode(false);
    setSpreadsheetZoomScale(1);
  }, []);

  const resetSpreadsheetSearch = useCallback(() => {
    setActiveSearchMatchIndex(0);
    setSpreadsheetSearchMatchCount(0);
    setSpreadsheetSearchQuery('');
  }, []);

  const handleSpreadsheetSearchToggle = useCallback(() => {
    setIsSpreadsheetSearchOpen(currentIsOpen => {
      if (currentIsOpen) {
        resetSpreadsheetSearch();
      }

      return !currentIsOpen;
    });
  }, [resetSpreadsheetSearch]);

  const handleSpreadsheetSearchQueryChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setActiveSearchMatchIndex(0);
    setSpreadsheetSearchQuery(event.target.value);
  }, []);

  const goToSpreadsheetSearchMatch = useCallback(
    (direction: 1 | -1) => {
      setActiveSearchMatchIndex(currentIndex => {
        if (!spreadsheetSearchMatchCount) {
          return 0;
        }

        return (currentIndex + direction + spreadsheetSearchMatchCount) % spreadsheetSearchMatchCount;
      });
    },
    [spreadsheetSearchMatchCount]
  );

  const handleSpreadsheetSearchKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        goToSpreadsheetSearchMatch(event.shiftKey ? -1 : 1);
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        setIsSpreadsheetSearchOpen(false);
        resetSpreadsheetSearch();
      }
    },
    [goToSpreadsheetSearchMatch, resetSpreadsheetSearch]
  );

  const handleWorkbookKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (
        !resolvedFeatureOptions.search ||
        event.key.toLocaleLowerCase() !== 'f' ||
        (!event.metaKey && !event.ctrlKey)
      ) {
        return;
      }

      event.preventDefault();
      setIsSpreadsheetSearchOpen(true);
    },
    [resolvedFeatureOptions.search]
  );

  // Let Cmd/Ctrl+F target the spreadsheet viewer while preserving stricter scope for embedded page previews.
  useEffect(() => {
    if (!resolvedFeatureOptions.search) {
      return undefined;
    }

    const viewerElement = viewerRef.current;
    const targetWindow = viewerElement?.ownerDocument.defaultView;
    if (!viewerElement || !targetWindow) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        !isFindKeyboardShortcut(event) ||
        !isScopedViewerKeyboardShortcut(viewerElement, event, {
          allowWhenViewerMounted: searchShortcutScope === 'mounted',
        })
      ) {
        return;
      }

      event.preventDefault();
      setIsSpreadsheetSearchOpen(true);
    };

    targetWindow.addEventListener('keydown', handleKeyDown);
    return () => targetWindow.removeEventListener('keydown', handleKeyDown);
  }, [resolvedFeatureOptions.search, searchShortcutScope]);

  // Re-fit spreadsheets only while users have explicitly selected Fit mode.
  useEffect(() => {
    if (!resolvedFeatureOptions.zoom || !isSpreadsheetFitMode) {
      return undefined;
    }

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || typeof ResizeObserver === 'undefined') {
      setSpreadsheetZoomScale(calculateSpreadsheetFitZoomScale());
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      setSpreadsheetZoomScale(calculateSpreadsheetFitZoomScale());
    });

    resizeObserver.observe(scrollContainer);
    setSpreadsheetZoomScale(calculateSpreadsheetFitZoomScale());

    return () => resizeObserver.disconnect();
  }, [activeTabIndex, calculateSpreadsheetFitZoomScale, isSpreadsheetFitMode, resolvedFeatureOptions.zoom]);

  // Keep XLSX indicators and sticky reference panes aligned while the workbook scrolls.
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const activeContent = activeSpreadsheetContentElement;
    if (!scrollContainer || !activeContent) {
      return undefined;
    }

    const ownerWindow = scrollContainer.ownerDocument.defaultView ?? window;
    const MutationObserverConstructor = ownerWindow.MutationObserver;
    const ResizeObserverConstructor = ownerWindow.ResizeObserver;
    let animationFrame = 0;

    const syncFrozenPanePosition = () => {
      animationFrame = 0;
      const titleColumnsContainer = activeContent.querySelector<HTMLElement>('.titles-columns-container');
      const titleColumnsWidth = titleColumnsContainer?.offsetWidth ?? 0;
      const selectedCell = activeContent.querySelector<HTMLElement>(
        '[data-testid="spreadsheet-note-xlsx-preview"] td.selected'
      );
      const visibleColumnIndicator = Array.from(
        activeContent.querySelectorAll<HTMLElement>(
          '[data-testid="spreadsheet-note-xlsx-preview"] [id^="column-indicator-"]'
        )
      ).find(indicator => {
        const indicatorRect = indicator.getBoundingClientRect();
        return ownerWindow.getComputedStyle(indicator).display !== 'none' && indicatorRect.width > 0;
      });

      if (selectedCell && visibleColumnIndicator) {
        const selectedCellRect = selectedCell.getBoundingClientRect();
        const columnIndicatorRect = visibleColumnIndicator.getBoundingClientRect();
        const visualScale = getElementVisualScale(selectedCell);
        const visualDelta = selectedCellRect.left - columnIndicatorRect.left;
        const currentTranslateX =
          Number.parseFloat(activeContent.style.getPropertyValue('--xlsx-column-indicator-translate-x')) || 0;

        if (visualScale > 0 && Math.abs(visualDelta) > 0.5) {
          activeContent.style.setProperty(
            '--xlsx-column-indicator-translate-x',
            `${currentTranslateX + visualDelta / visualScale}px`
          );
        }
      } else {
        activeContent.style.setProperty(
          '--xlsx-column-indicator-translate-x',
          `${titleColumnsWidth - scrollContainer.scrollLeft}px`
        );
      }

      if (shouldEnableFrozenReferenceColumn) {
        activeContent.style.setProperty('--xlsx-title-columns-translate-x', '0px');
        const hasFrozenColumnReachedEdge = scrollContainer.scrollLeft >= XLSX_ROW_INDICATOR_COVER_WIDTH;

        if (!hasFrozenColumnReachedEdge) {
          activeContent.removeAttribute('data-xlsx-frozen-column-stuck');
          activeContent.style.setProperty('--xlsx-frozen-column-border-color', '#D8E3EE');
          activeContent.style.setProperty('--xlsx-frozen-column-border-width', '1px');
          activeContent.style.setProperty('--xlsx-frozen-column-shadow', 'none');
          activeContent.style.setProperty('--xlsx-frozen-column-shadow-accent', '0 0 0 0 transparent');
        } else {
          activeContent.setAttribute('data-xlsx-frozen-column-stuck', 'true');
          activeContent.style.setProperty('--xlsx-frozen-column-border-color', 'rgba(3, 125, 232, 0.48)');
          activeContent.style.setProperty('--xlsx-frozen-column-border-width', '3px');
          activeContent.style.setProperty('--xlsx-frozen-column-shadow', '3px 0 10px rgba(3, 125, 232, 0.14)');
          activeContent.style.setProperty('--xlsx-frozen-column-shadow-accent', '3px 0 10px rgba(3, 125, 232, 0.14)');
        }

        activeContent.style.setProperty('--xlsx-frozen-column-left', `${Math.max(0, titleColumnsWidth)}px`);
      }

      if (shouldEnableFrozenReferenceRow) {
        if (scrollContainer.scrollTop <= XLSX_FROZEN_REFERENCE_ROW_TOP) {
          activeContent.style.setProperty('--xlsx-frozen-row-border-color', '#D8E3EE');
          activeContent.style.setProperty('--xlsx-frozen-row-border-width', '1px');
          activeContent.style.setProperty('--xlsx-frozen-row-shadow', 'none');
        } else {
          activeContent.style.setProperty('--xlsx-frozen-row-border-color', 'rgba(3, 125, 232, 0.48)');
          activeContent.style.setProperty('--xlsx-frozen-row-border-width', '2px');
          activeContent.style.setProperty('--xlsx-frozen-row-shadow', '0 3px 10px rgba(3, 125, 232, 0.14)');
        }
      }
    };

    const requestFrozenPaneSync = () => {
      if (animationFrame) {
        return;
      }
      animationFrame = ownerWindow.requestAnimationFrame(syncFrozenPanePosition);
    };

    syncFrozenPanePosition();
    scrollContainer.addEventListener('scroll', requestFrozenPaneSync, { passive: true });
    const resizeObserver = ResizeObserverConstructor ? new ResizeObserverConstructor(requestFrozenPaneSync) : null;
    resizeObserver?.observe(activeContent);
    const mutationObserver = MutationObserverConstructor
      ? new MutationObserverConstructor(requestFrozenPaneSync)
      : null;
    mutationObserver?.observe(activeContent, {
      attributeFilter: [
        'class',
        'data-xlsx-frozen-pane-column',
        'data-xlsx-frozen-pane-row',
        'data-xlsx-frozen-pane-column-boundary',
        'data-xlsx-frozen-pane-row-boundary',
      ],
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => {
      scrollContainer.removeEventListener('scroll', requestFrozenPaneSync);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      activeContent.style.removeProperty('--xlsx-frozen-column-left');
      activeContent.style.removeProperty('--xlsx-column-indicator-translate-x');
      activeContent.style.removeProperty('--xlsx-title-columns-translate-x');
      activeContent.style.removeProperty('--xlsx-frozen-column-border-color');
      activeContent.style.removeProperty('--xlsx-frozen-column-border-width');
      activeContent.style.removeProperty('--xlsx-frozen-column-shadow');
      activeContent.style.removeProperty('--xlsx-frozen-column-shadow-accent');
      activeContent.removeAttribute('data-xlsx-frozen-column-stuck');
      activeContent.style.removeProperty('--xlsx-frozen-row-border-color');
      activeContent.style.removeProperty('--xlsx-frozen-row-border-width');
      activeContent.style.removeProperty('--xlsx-frozen-row-shadow');
      if (animationFrame) {
        ownerWindow.cancelAnimationFrame(animationFrame);
      }
    };
  }, [
    activeSpreadsheetContentElement,
    activeTabIndex,
    effectiveSpreadsheetZoomScale,
    shouldEnableFrozenReferenceColumn,
    shouldEnableFrozenReferenceRow,
  ]);

  // Reapply the selected freeze boundary when the active sheet or viewer scale changes.
  useEffect(() => {
    if (
      contentVariant !== 'legacy-data-grid' ||
      !activeSpreadsheetContentElement ||
      (!freezePaneColumnCellKey && !freezePaneRowCellKey)
    ) {
      return undefined;
    }

    return applySpreadsheetFreezePane({
      activeContent: activeSpreadsheetContentElement,
      columnCellKey: shouldEnableFrozenReferenceColumn ? freezePaneColumnCellKey : undefined,
      rowCellKey: shouldEnableFrozenReferenceRow ? freezePaneRowCellKey : undefined,
    });
  }, [
    activeSpreadsheetContentElement,
    activeTabIndex,
    contentVariant,
    effectiveSpreadsheetZoomScale,
    freezePaneColumnCellKey,
    freezePaneRowCellKey,
    shouldEnableFrozenReferenceColumn,
    shouldEnableFrozenReferenceRow,
  ]);

  const spreadsheetSearchActions = useMemo(() => {
    if (!resolvedFeatureOptions.search) {
      return null;
    }

    const searchMatchLabel = normalizedSearchQuery
      ? `${spreadsheetSearchMatchCount ? activeSearchMatchIndex + 1 : 0}/${spreadsheetSearchMatchCount}`
      : '0/0';

    return (
      <SpreadsheetViewerSearchToolbarSection zoomLevel={searchControlsZoomLevel}>
        <SpreadsheetViewerToolbarButton
          aria-label={isSpreadsheetSearchOpen ? 'Close spreadsheet search' : 'Search spreadsheet'}
          onClick={handleSpreadsheetSearchToggle}
          style={{ minWidth: getZoomAdjustedPx(XLSX_SEARCH_BUTTON_WIDTH, searchControlsZoomLevel) }}
          zoomLevel={searchControlsZoomLevel}
          type="button">
          {isSpreadsheetSearchOpen ? <CloseRounded /> : <SearchOutlined />}
        </SpreadsheetViewerToolbarButton>
        {isSpreadsheetSearchOpen ? (
          <>
            <SpreadsheetViewerSearchInput
              ref={searchInputRef}
              aria-label="Search spreadsheet"
              onChange={handleSpreadsheetSearchQueryChange}
              onKeyDown={handleSpreadsheetSearchKeyDown}
              placeholder="Search in sheet"
              value={spreadsheetSearchQuery}
              zoomLevel={searchControlsZoomLevel}
            />
            <SpreadsheetViewerToolbarText
              style={{
                minWidth: getZoomAdjustedPx(XLSX_SEARCH_COUNTER_WIDTH, searchControlsZoomLevel),
                textAlign: 'center',
              }}
              zoomLevel={searchControlsZoomLevel}>
              {searchMatchLabel}
            </SpreadsheetViewerToolbarText>
            <SpreadsheetViewerToolbarButton
              aria-label="Previous spreadsheet search result"
              disabled={!spreadsheetSearchMatchCount}
              onClick={() => goToSpreadsheetSearchMatch(-1)}
              style={{ minWidth: getZoomAdjustedPx(XLSX_SEARCH_BUTTON_WIDTH, searchControlsZoomLevel) }}
              zoomLevel={searchControlsZoomLevel}
              type="button">
              <KeyboardArrowUpRounded />
            </SpreadsheetViewerToolbarButton>
            <SpreadsheetViewerToolbarButton
              aria-label="Next spreadsheet search result"
              disabled={!spreadsheetSearchMatchCount}
              onClick={() => goToSpreadsheetSearchMatch(1)}
              style={{ minWidth: getZoomAdjustedPx(XLSX_SEARCH_BUTTON_WIDTH, searchControlsZoomLevel) }}
              zoomLevel={searchControlsZoomLevel}
              type="button">
              <KeyboardArrowDownRounded />
            </SpreadsheetViewerToolbarButton>
          </>
        ) : null}
      </SpreadsheetViewerSearchToolbarSection>
    );
  }, [
    activeSearchMatchIndex,
    goToSpreadsheetSearchMatch,
    handleSpreadsheetSearchKeyDown,
    handleSpreadsheetSearchQueryChange,
    handleSpreadsheetSearchToggle,
    isSpreadsheetSearchOpen,
    normalizedSearchQuery,
    resolvedFeatureOptions.search,
    searchControlsZoomLevel,
    spreadsheetSearchMatchCount,
    spreadsheetSearchQuery,
  ]);

  const spreadsheetZoomActions = useMemo(() => {
    if (!resolvedFeatureOptions.zoom) {
      return null;
    }

    const zoomPercentage = Math.round(spreadsheetZoomScale * 100);

    return (
      <SpreadsheetViewerToolbarSection zoomLevel={controlsZoomLevel}>
        <SpreadsheetViewerToolbarButton
          aria-label="Zoom spreadsheet out"
          disabled={spreadsheetZoomScale <= XLSX_MIN_ZOOM}
          onClick={handleSpreadsheetZoomOut}
          zoomLevel={controlsZoomLevel}
          type="button">
          <RemoveRounded />
        </SpreadsheetViewerToolbarButton>
        <SpreadsheetViewerToolbarText
          style={{ minWidth: getZoomAdjustedPx(42, controlsZoomLevel), textAlign: 'center' }}
          zoomLevel={controlsZoomLevel}>
          {zoomPercentage}%
        </SpreadsheetViewerToolbarText>
        <SpreadsheetViewerToolbarButton
          aria-label="Zoom spreadsheet in"
          disabled={spreadsheetZoomScale >= XLSX_MAX_ZOOM}
          onClick={handleSpreadsheetZoomIn}
          zoomLevel={controlsZoomLevel}
          type="button">
          <AddRounded />
        </SpreadsheetViewerToolbarButton>
        <SpreadsheetViewerToolbarDivider aria-hidden="true" zoomLevel={controlsZoomLevel} />
        <SpreadsheetViewerToolbarButton
          aria-label="Fit spreadsheet to preview width"
          onClick={handleSpreadsheetFit}
          zoomLevel={controlsZoomLevel}
          type="button">
          Fit
        </SpreadsheetViewerToolbarButton>
        <SpreadsheetViewerToolbarButton
          aria-label="Reset spreadsheet zoom"
          disabled={spreadsheetZoomScale === 1 && !isSpreadsheetFitMode}
          onClick={handleSpreadsheetActualSize}
          zoomLevel={controlsZoomLevel}
          type="button">
          Reset
        </SpreadsheetViewerToolbarButton>
      </SpreadsheetViewerToolbarSection>
    );
  }, [
    handleSpreadsheetActualSize,
    handleSpreadsheetFit,
    handleSpreadsheetZoomIn,
    handleSpreadsheetZoomOut,
    controlsZoomLevel,
    isSpreadsheetFitMode,
    resolvedFeatureOptions.zoom,
    spreadsheetZoomScale,
  ]);

  const workbookActions = useMemo(() => {
    if (!actions && !spreadsheetSearchActions && !spreadsheetZoomActions) {
      return undefined;
    }

    const leadingActions = (
      <>
        {spreadsheetSearchActions}
        {spreadsheetZoomActions}
      </>
    );

    const renderedActions =
      typeof actions === 'function' ? (
        actions(leadingActions)
      ) : (
        <>
          {leadingActions}
          {actions}
        </>
      );

    return (
      <SpreadsheetViewerWorkbookActions zoomLevel={controlsZoomLevel}>
        {renderedActions}
      </SpreadsheetViewerWorkbookActions>
    );
  }, [actions, controlsZoomLevel, spreadsheetSearchActions, spreadsheetZoomActions]);

  if (isLoading) {
    return (
      <SpreadsheetWorkbookViewer ariaLabel={ariaLabel} isLoading loadingMessage={loadingMessage} testId={testId} />
    );
  }

  if (error) {
    return (
      fallback ?? (
        <SpreadsheetWorkbookViewer
          ariaLabel={ariaLabel}
          errorTitle={errorTitle}
          errorMessage={errorMessage}
          testId={testId}
        />
      )
    );
  }

  if (!tabs.length) {
    return (
      fallback ?? (
        <SpreadsheetWorkbookViewer
          ariaLabel={ariaLabel}
          emptyTitle={emptyTitle}
          emptyMessage={emptyMessage}
          testId={testId}
        />
      )
    );
  }

  return (
    <div ref={viewerRef} style={{ height: '100%', minHeight: 0 }}>
      <WorkbookContext.Provider value={workbookContextValue}>
        <SpreadsheetWorkbookViewer
          actions={workbookActions}
          ariaLabel={ariaLabel}
          contentVariant={contentVariant}
          navigationZoomLevel={controlsZoomLevel}
          onKeyDown={handleWorkbookKeyDown}
          onSheetChange={handleSheetTabChange}
          scrollContainerProps={{
            style: {
              '--xlsx-preview-zoom-scale': effectiveSpreadsheetZoomScale,
            } as CSSProperties,
          }}
          scrollContainerRef={scrollContainerRef}
          sheetItems={sheetTabs}
          testId={testId}
          value={activeSheetTabValue}>
          {tabs.map((tab, tabIndex) => {
            const isActive = activeTabIndex === tabIndex;
            const isMounted = isActive || openedTabIndexes.has(tabIndex);

            if (!isMounted) {
              return null;
            }

            return (
              <div
                key={getSpreadsheetSheetTabValue(tab.id, tabIndex, tabValuePrefix)}
                role="tabpanel"
                aria-hidden={!isActive}
                style={{ display: isActive ? 'block' : 'none', minHeight: '100%', minWidth: '100%' }}>
                <div
                  ref={isActive ? handleActiveSpreadsheetContentRef : undefined}
                  data-xlsx-frozen-reference-column={shouldEnableFrozenReferenceColumn ? 'true' : undefined}
                  data-xlsx-frozen-reference-row={shouldEnableFrozenReferenceRow ? 'true' : undefined}
                  style={{
                    display: 'inline-block',
                    minHeight: '100%',
                    minWidth: effectiveSpreadsheetZoomScale < 1 ? `${100 / effectiveSpreadsheetZoomScale}%` : '100%',
                    transformOrigin: 'top left',
                    zoom: effectiveSpreadsheetZoomScale,
                  }}>
                  {tab.content}
                </div>
              </div>
            );
          })}
        </SpreadsheetWorkbookViewer>
      </WorkbookContext.Provider>
    </div>
  );
};

export default SpreadsheetWorkbookPreview;
